/**
 * @module Separator
 *
 * Visual divider for separating content. Built on Radix UI Separator primitive.
 *
 * @see {@link https://ui.shadcn.com/docs/components/separator Shadcn Separator}
 * @see {@link https://www.radix-ui.com/primitives/docs/components/separator Radix Separator}
 *
 * @example
 * // Horizontal separator
 * <Separator />
 *
 * @example
 * // Vertical separator
 * <Separator orientation="vertical" className="h-4" />
 *
 * @example
 * // Between sections
 * <div>Section 1</div>
 * <Separator className="my-4" />
 * <div>Section 2</div>
 */
import { Separator as SeparatorPrimitive } from "radix-ui";
import type { Ref } from "react";
import { cn } from "../lib/utils";

/**
 * Visual divider line, horizontal or vertical.
 * @param orientation - `"horizontal"` (default) or `"vertical"`
 * @param decorative - If true, removes from accessibility tree (default: true)
 */
interface SeparatorProps extends React.ComponentPropsWithoutRef<typeof SeparatorPrimitive.Root> {
  ref?: Ref<React.ElementRef<typeof SeparatorPrimitive.Root>>;
}
const Separator = ({
  className,
  orientation = "horizontal",
  decorative = true,
  ref,
  ...props
}: SeparatorProps) => <SeparatorPrimitive.Root className={cn("shrink-0 bg-gray-150 dark:bg-gray-800", orientation === "horizontal" ? "h-px w-full" : "h-full w-px", className)} decorative={decorative} orientation={orientation} ref={ref} {...props} data-sentry-element="SeparatorPrimitive.Root" data-sentry-component="Separator" data-sentry-source-file="separator.tsx" />;
export { Separator };